// Library methods
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { TrashIcon } from "src/assets/icons";

// Styles
import {
  ModalCustomCancel,
  getRequiredErrorColor,
  getSurfaceTokens,
  noHoverBackgroundSx,
} from "src/styles/mui/muiStylesHelper";

type SystemCardType = {
  testId?: string;
  icon: ReactNode;
  name: string;
  subtitle?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  children: ReactNode;
};

const SystemCard = ({
  testId,
  icon,
  name,
  subtitle,
  onEdit,
  onDelete,
  children,
}: SystemCardType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface, mutedText } = getSurfaceTokens(theme);
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      component="article"
      aria-label={name}
      data-testid={testId}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        padding: "14px",
        height: "100%",
        boxSizing: "border-box",
        borderRadius: "8px",
        backgroundColor: isDark ? "#1C1D29" : "rgba(255, 255, 255, 0.7)",
        border: isDark ? "1px solid #34364A" : "1px solid rgba(0, 0, 0, 0.2)",
      }}
    >
      <Box display="flex" gap="12px" alignItems="flex-start">
        <Box
          aria-hidden="true"
          sx={{
            width: "40px",
            height: "40px",
            flexShrink: 0,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: onSurface,
            backgroundColor: isDark ? "#2A2C3F" : "rgba(0, 0, 0, 0.06)",
          }}
        >
          {icon}
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography
            component="h3"
            fontSize="15px"
            fontWeight={700}
            color={onSurface}
            sx={{ wordBreak: "break-word" }}
          >
            {name}
          </Typography>
          {subtitle && (
            <Typography
              fontSize="12px"
              color={mutedText}
              sx={{ wordBreak: "break-word" }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {onEdit && (
          <Button
            sx={{ ...ModalCustomCancel(theme), height: "36px", minWidth: 0 }}
            onClick={onEdit}
            aria-label={`${t("Edit")} ${name}`}
          >
            {t("Edit")}
          </Button>
        )}
        {onDelete && (
          <Tooltip title={t("Delete Connection")}>
            <IconButton
              aria-label={`${t("Delete")} ${name}`}
              onClick={onDelete}
              sx={{
                color: getRequiredErrorColor(theme),
                ...noHoverBackgroundSx,
              }}
            >
              <TrashIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {children}
    </Box>
  );
};

export default SystemCard;
